/* HerbIQ Formulator — client (ADR-014 Step 3).
 *
 * This client holds NO matrix, identity or graph decision data. Stage 1 (formulation) and Stage 2
 * (sourcing) run SERVER-SIDE behind the Shopify App Proxy → Cloudflare Worker; the browser only collects
 * inputs, calls the two endpoints, and renders the minimal response. The only data embedded here is UI
 * vocabulary: product/role labels for the pickers and format-code display labels. There is no window.HB_MX
 * dependency (herbuno-matrix.js is no longer loaded in production).
 *
 * Pure functions (request building, response→HTML rendering, version check) are exported under Node for
 * tests; the DOM/fetch bootstrap runs only in the browser. */
(function () {
  'use strict';
  var IS_BROWSER = typeof document !== 'undefined' && typeof window !== 'undefined';

  // The client rejects any response whose schema is not exactly this — never renders a partial/guessed result.
  // v3: the response carries a `message` block { category, header, body, why, technical_status } (messaging
  // taxonomy) in place of the old specification/explanation fields.
  var API_SCHEMA_VERSION = 3;
  var SPEC_ENDPOINT = '/apps/formulator/specification';
  var PROC_ENDPOINT = '/apps/formulator/procurement';

  var DEGRADED_FALLBACK = 'HerbIQ Formulator is temporarily unable to generate the technical specification. ' +
    'Your selections have been preserved; please retry shortly or send them to Herbuno for review.';
  var BUILD_STUB = 'Full blend builder is being upgraded — the multi-ingredient workspace is being migrated ' +
    'to the secure server-side knowledge layer. For now, use Check one ingredient.';

  // ---- UI vocabulary ONLY (NOT the decision matrix): format-code display labels ----
  var FORMAT_LABELS = { MP: 'Milled powder', RE: 'Ratio (full-spectrum) extract', SE: 'Standardised extract',
    WL: 'Water-soluble extract', OE: 'Oil-soluble extract', WD: 'Whole / cut material', CO: 'Carrier oil',
    SD: 'Spray-dried powder', TC: 'Cut botanical' };
  // Candidate picker: UI label -> code sent. The label is NEVER the contract. 'Other' is never sent
  // (free text in the enquiry only). "Liquid extract" is ambiguous and deliberately NOT offered.
  var CANDIDATE_OPTIONS = [ { label: 'Milled powder', code: 'MP' }, { label: 'Ratio extract', code: 'RE' },
    { label: 'Standardised extract', code: 'SE' }, { label: 'Water-soluble extract', code: 'WL' },
    { label: 'Oil-soluble extract', code: 'OE' }, { label: 'Whole / cut material', code: 'WD' },
    { label: 'Other', code: null } ];
  // PRESENTATION grouping for the picker (owner vocabulary ruling). SE/RE describe an extract's
  // specification (assay / ratio), not a physical state, so they are separated from the form options
  // rather than listed alongside them. Grouping and group LABELS only — the same six codes are offered,
  // the same values are sent, and 'Other' keeps its sentinel behaviour (never sent to the server).
  // CANDIDATE_OPTIONS above stays the single source of code->label; this only says where each code sits.
  var CANDIDATE_GROUPS = [
    { label: 'Form / delivery type', codes: ['MP', 'WL', 'OE', 'WD'] },
    { label: 'Extract specification', codes: ['SE', 'RE'] },
  ];
  // ACTIVE role ontology = exactly the six role keys present in the governed 129-cell matrix
  // (active, base, colour, flavour, functional, texture). `carrier` was removed (owner ruling): it was an
  // inherited compatibility term from the earlier 34-product concept, never a role key in any revision of
  // herbuno-matrix.js, and mapped in 0 of 39 products — so it could only ever dead-end.
  // Coverage stays REACTIVE (Ruling 2): all six are offered for every product and an uncovered
  // product x role is still discovered from the Worker's not_available_for_product guidance.
  var ROLES = [ { id: 'base', label: 'Base', desc: 'the primary carrier or body of the product' },
    { id: 'active', label: 'Active', desc: 'the ingredient delivering the main intended benefit' },
    { id: 'functional', label: 'Functional', desc: 'fibre or bulking ingredient' },
    { id: 'flavour', label: 'Flavour', desc: 'flavour or aroma' },
    { id: 'texture', label: 'Texture', desc: 'texture or structure' },
    { id: 'colour', label: 'Colour', desc: 'colour' } ];
  // `desc` — owner-approved family description (27 Sep 2026). Every product type it names traces to a display
  // label in that family's own items (mapping in the Gutins-delta PR). No benefit/health/quality claims.
  var PRODUCTS = [
    { fam: 'Oral Solids', desc: 'Capsules, softgels, tablets, gummies, lozenges and pastilles.', items: [{ id: 'capsule', name: 'Hard Capsule — conventional dry fill' }, { id: 'softgel', name: 'Softgel — oil-based fill' }, { id: 'tablet-dc', name: 'Tablet — Direct Compression' }, { id: 'tablet-wg', name: 'Tablet — Wet Granulation' }, { id: 'effervescent', name: 'Effervescent Tablet' }, { id: 'gummy', name: 'Gummy / Chewable' }, { id: 'lozenge-c', name: 'Lozenge — Compressed' }, { id: 'pastille', name: 'Pastille — Cooked' }] },
    { fam: 'Oral Powders', desc: 'Instant beverages, drink sachets and scoop blends.', items: [{ id: 'instant-hot', name: 'Instant Beverage — Hot / Opaque or Cloudy' }, { id: 'instant-cold', name: 'Instant Beverage — Cold / Opaque or Cloudy' }, { id: 'sachet', name: 'Dispersible Sachet / Drink Powder' }, { id: 'scoop', name: 'Scoop / Bulk Blend' }] },
    { fam: 'Oral Liquids', desc: 'Ready-to-drink liquids, drops, syrups, oral sprays and shots.', items: [{ id: 'rtd-clear', name: 'RTD — Clear' }, { id: 'rtd-cloudy', name: 'RTD — Cloudy / Suspension' }, { id: 'drops-aq', name: 'Drops — Aqueous / Hydroalcoholic' }, { id: 'drops-oil', name: 'Drops — Oil-based' }, { id: 'syrup', name: 'Syrup — uniform solution' }, { id: 'spray', name: 'Oral Spray' }, { id: 'shot', name: 'Shot / Concentrate' }] },
    { fam: 'Food & Beverage', desc: 'Bakery, snacks, chocolate coatings, dairy, plant milk, tea and seasoning.', items: [{ id: 'bakery', name: 'Bakery & Snack Fortification' }, { id: 'confectionery', name: 'Chocolate / Fat-based Confectionery Coating' }, { id: 'dairy', name: 'Dairy & Plant Milk' }, { id: 'tea', name: 'Tea & Infusion — Brewed/Strained' }, { id: 'savoury', name: 'Savoury, Culinary & Seasoning' }] },
    { fam: 'Cosmetic & Topical', desc: 'Serums, toners, creams, face and hair oils, balms, cleansers and masks.', items: [{ id: 'serum', name: 'Clear Aqueous Serum / Toner' }, { id: 'cream', name: 'Cream / Lotion' }, { id: 'face-oil', name: 'Face & Hair Oil' }, { id: 'balm', name: 'Balm / Stick / Bar' }, { id: 'cleanser', name: 'Shampoo / Cleanser' }, { id: 'mask-dry', name: 'Mask — Dry Powder' }, { id: 'mask-wet', name: 'Mask — Preserved Wet' }] },
    { fam: 'Traditional & Specialty', desc: 'Ayurvedic taila, churna, kashaya; aromatherapy, fragrance, pet, R&D.', items: [{ id: 'taila', name: 'Taila — medicated oil' }, { id: 'churna', name: 'Churna — classical powder' }, { id: 'kashaya', name: 'Kashaya — decoction' }, { id: 'aroma-diff', name: 'Aromatherapy — Room Diffuser' }, { id: 'aroma-roll', name: 'Aromatherapy — Topical Roll-on' }, { id: 'ff', name: 'Fragrance & Flavour System (B2B)' }, { id: 'pet', name: 'Pet Supplement' }, { id: 'rnd', name: 'R&D Pilot & Sampling' }] },
  ];

  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function productName(id) { for (var i = 0; i < PRODUCTS.length; i++) { var it = PRODUCTS[i].items; for (var j = 0; j < it.length; j++) if (it[j].id === id) return it[j].name; } return id; }
  function roleLabel(id) { for (var i = 0; i < ROLES.length; i++) if (ROLES[i].id === id) return ROLES[i].label; return id; }
  function formLabel(code) { return code ? (FORMAT_LABELS[code] || code) : 'Application review needed'; }
  function candidateLabel(code) { for (var i = 0; i < CANDIDATE_OPTIONS.length; i++) if (CANDIDATE_OPTIONS[i].code === code) return CANDIDATE_OPTIONS[i].label; return FORMAT_LABELS[code] || code; }

  // ---- pure: enquiry (RFQ) body ----
  // SHARED by the enquiry modal's context block and its "Copy details" control (via enquiryText()).
  // Kept pure + exported so the generated lines stay unit-tested. The former buildMailto() companion was
  // removed with the Open-in-Email control (owner ruling, G3); these lines are unchanged by that removal.
  function enquiryLines(state) {
    return 'Product: ' + productName(state.product) + '\nRole: ' + roleLabel(state.role) + '\nBotanical: ' + ((state.botanical || '').trim()) +
      (state.candidate && state.candidate !== 'OTHER' ? '\nFormat in mind: ' + candidateLabel(state.candidate) : '');
  }
  // ---- pure: ACTION CONTEXT (G2 row-context fix) ----
  // The row-shaped contract { product, role, botanical, candidate } that enquiryLines() and
  // buildSpecBody() already consume. Two producers, ONE shape, so downstream actions never need to know
  // which mode they were invoked from — and a blend action can never fall back to ambient single-mode S.
  // Both are PURE (no DOM, no S): the DOM row -> ing lookup stays in the click handler where it belongs.
  // Deliberately NOT carrying resp/rowId/token/isBlend — no in-scope Option-A action needs them, and
  // blend retry reuses runOne(ing), which already owns its own identity/generation protection.
  // Botanical is NOT trimmed here: enquiryLines()/buildSpecBody() already trim, and the selection-copy
  // string has never trimmed. Trimming here would silently change existing single-mode copy output.
  function blendRowContext(blend, ing) {
    return { product: blend.product, role: ing.role, botanical: ing.botanical, candidate: ing.candidate };
  }
  // Q2 PREFLIGHT body. Asks one question — does this product x role have governed coverage — and carries
  // nothing else: no botanical, no candidate_format. The Worker stays the sole coverage authority; the
  // answer is never cached or turned into a client coverage map.
  function buildPreflightBody(state) { return { product: state.product, role: state.role, preflight: true }; }
  function singleContext(state) {
    return { product: state.product, role: state.role, botanical: state.botanical, candidate: state.candidate };
  }
  // ---- pure: "Copy my selections" text (was built inline in the click handler, reading S directly) ----
  function copySelectionText(ctx) {
    return 'Product: ' + productName(ctx.product) + ' · Role: ' + roleLabel(ctx.role) + ' · Botanical: ' + (ctx.botanical || '');
  }
  // ---- pure: request body (candidate_format sent ONLY when the user actually chose one) ----
  function buildSpecBody(state) {
    var b = { product: state.product, role: state.role, botanical: (state.botanical || '').trim() };
    // 'Other' (sentinel 'OTHER') and empty are NEVER sent — 'Other' is free text in the enquiry only.
    if (state.candidate && state.candidate !== 'OTHER') b.candidate_format = state.candidate;
    return b;
  }
  // ---- pure: schema compatibility (reject incompatible responses in EITHER direction) ----
  function apiCompatible(resp) { return !!(resp && resp.version && resp.version.api_schema_version === API_SCHEMA_VERSION); }

  // ---- pure: ADR-015 §2 format qualifier (OWNER-APPROVED vocabulary, 26 Jul 2026) ----
  // SHOW: SE/RE -> "dry powder", OE -> "oil-soluble liquid". SUPPRESS (label already states the form):
  // WL, MP, CO, SD, TC, WD. Future additions require OWNER APPROVAL. The Worker sends the selected format
  // CODE (resp.recommended_format); the client maps it here, then applies deterministic §4.3 suppression.
  var QUALIFIER_BY_CODE = { SE: 'dry powder', RE: 'dry powder', OE: 'oil-soluble liquid' };
  var MATERIALLY_INFORMATIVE = ['dry powder', 'oil-soluble liquid'];
  function ingredientHasEquivalent(ingredient, nf) {
    var ing = String(ingredient).toLowerCase();
    if (/powder/.test(ing) && /powder/.test(nf)) return true;         // don't restate "powder"
    if (/\bliquid\b/.test(ing) && /\bliquid\b/.test(nf)) return true; // don't restate "liquid"
    return false;
  }
  // Returns the qualifier string to display, or null to SUPPRESS. `nonValues` = Set of approved empty-state /
  // non-applicability strings (cond 1 & 3). Conditions numbered per ADR-015 §4.3.
  function formatQualifier(ingredient, recommendedFormat, nonValues) {
    if (!ingredient || (nonValues && nonValues.has(ingredient))) return null;   // cond 1
    if (!recommendedFormat) return null;                                        // cond 2
    if (nonValues && nonValues.has(recommendedFormat)) return null;             // cond 3
    var nf = String(recommendedFormat).toLowerCase().trim();
    if (MATERIALLY_INFORMATIVE.indexOf(nf) === -1) return null;                 // cond 4
    if (ingredientHasEquivalent(ingredient, nf)) return null;                   // cond 5
    return recommendedFormat;
  }

  // ---- pure: rendering (return HTML strings; no DOM access) ----
  function reasoningRow(k, v) { return '<div class="bb-rc-r"><span class="bb-rc-k">' + esc(k) + '</span><span class="bb-rc-v">' + esc(v) + '</span></div>'; }
  // The computed three-check body (no wrapper) — shared by the single V2 reasoning disclosure below.
  function checksBody(resp) {
    var rc = resp.reasoning_checks; if (!rc) return '';
    var note = resp.reasoning_basis === 'role' ? '<p class="bb-rc-note">Based on the product and role only — not this specific botanical.</p>' : '';
    return '<div class="bb-rc-body">' + note +
      reasoningRow('Phase compatibility', rc.phase) +
      reasoningRow('Dissolution / dispersion requirement', rc.dissolution) +
      reasoningRow('Process constraint', rc.process) +
      '</div>';
  }
  function renderReasoning(resp) {
    var b = checksBody(resp); if (!b) return '';
    return '<details class="bb-reasoning"><summary>How this result was reasoned</summary>' + b + '</details>';
  }
  // V2: ONE reasoning disclosure in the result action stack, "How this result was reasoned", holding the
  // authored reasoning (when the cell has it) followed by the computed checks (unless the authored payload
  // suppresses them). Nothing is dropped; the two former disclosures are presented as one.
  function reasoningDisclosure(resp) {
    var a = authoredBody(resp);
    var suppress = !!(resp.reasoning && resp.reasoning.suppress_computed_checks);
    var c = (resp.reasoning_checks && !suppress) ? checksBody(resp) : '';
    if (!a && !c) return '';
    return '<details class="bb-reasoning' + (a ? ' bb-reasoning-authored' : '') + '"><summary>How this result was reasoned</summary>' + a + c + '</details>';
  }
  // AUTHORED reasoning block — separate from the computed 3-check disclosure above. Absent (resp.reasoning
  // null) => no block: "no authored reasoning available yet" — the seven-row card is unaffected. Dispatch is
  // by SHAPE only (never category/type/cell): a hybrid payload has `pattern`/`cell`; the legacy RP-02 payload
  // has `what_spec_requires`. The legacy path retires when RP-02 migrates.
  function authoredRow(label, body) { return '<div class="bb-ar-r"><span class="bb-ar-k">' + esc(label) + '</span><p class="bb-ar-v">' + esc(body) + '</p></div>'; }
  function renderAuthoredReasoning(resp) {
    var b = authoredBody(resp); if (!b) return '';
    return '<details class="bb-reasoning bb-reasoning-authored"><summary>How this result was reasoned</summary>' + b + '</details>';
  }
  function authoredBody(resp) {
    var r = resp.reasoning; if (!r) return '';
    if (r.what_spec_requires !== undefined) return renderReasoningLegacy(r); // RP-02 (transitional)
    return renderReasoningV2(r);
  }
  // Hybrid model. The block renders ONLY when the cell has a non-empty chain; with no chain there is NO block,
  // even if a pattern principle exists (the computed-check disclosure then remains). Reads ONLY presence of
  // chain / caveats / governing_principle / shared_form_logic — never category, reasoning_type or cell_key.
  // Order when present: ordered chain · "Operational note" caveats · underlying principle (last, muted).
  // final_recommendation is intentionally NOT rendered — the seven-row "Ingredient to Source" row carries it.
  function principleRow(label, body) { return '<div class="bb-ar-principle"><span class="bb-ar-k">' + esc(label) + '</span><p class="bb-ar-v">' + esc(body) + '</p></div>'; }
  function renderReasoningV2(r) {
    var c = r.cell;
    if (!(c && c.chain && c.chain.length)) return ''; // no chain => no reasoning block at all
    var parts = ['<ol class="bb-chain">' + c.chain.map(function (s) { return '<li>' + esc(s.text) + '</li>'; }).join('') + '</ol>'];
    if (c.caveats && c.caveats.length) {
      parts.push('<div class="bb-cav-label">Operational note</div>' +
        '<ul class="bb-caveats">' + c.caveats.map(function (s) { return '<li>' + esc(s.text) + '</li>'; }).join('') + '</ul>');
    }
    if (r.pattern && r.pattern.governing_principle) parts.push(principleRow('Underlying principle', r.pattern.governing_principle));
    if (r.pattern && r.pattern.shared_form_logic) parts.push(principleRow('Shared form logic', r.pattern.shared_form_logic));
    return '<div class="bb-ar-body">' + parts.join('') + '</div>';
  }
  // Legacy five-field block (RP-02 only, until migrated). Fixed labels for fields 1/2/5; slot3_heading verbatim.
  function renderReasoningLegacy(r) {
    return '<div class="bb-ar-body">' +
      authoredRow('What the specification requires', r.what_spec_requires) +
      authoredRow('Why the recommended form fits', r.why_form_fits) +
      authoredRow(r.slot3_heading, r.slot3_body) +
      authoredRow('Governing principle', r.governing_principle) +
      '</div>';
  }
  // Style from the Worker's AUTHORITATIVE `severity` (ok | warn | avoid | neutral). Never re-infer pass/fail
  // from the status wording — "Not evaluated for this role"/"Needs a physical form" are neutral and must NOT
  // read as a pass (Live-test R2 BUG 4). `neutral` -> the 'review' (caution) style. The regex below is a
  // fallback ONLY for a response that predates `severity`; it matches the pre-ruling wording and does not
  // recognise the current strings, which is safe because it defaults to 'review' — never 'ok'.
  function candidateStatusClass(ca) {
    var sev = ca && ca.severity;
    if (sev === 'ok' || sev === 'warn' || sev === 'avoid') return sev;
    if (sev === 'neutral') return 'review';
    var status = (ca && ca.technical_status) || '';
    if (/^Best/.test(status)) return 'ok';
    if (/^Conditional/.test(status)) return 'warn';
    if (/^Not suitable/.test(status)) return 'avoid';
    return 'review';
  }
  function renderCandidate(resp) {
    var ca = resp.candidate_assessment; if (!ca) return '';
    return '<div class="bb-cand ' + candidateStatusClass(ca) + '">' +
      '<div class="bb-cand-r"><span class="bb-cand-k">Your selection</span><span class="bb-cand-v">' + esc(candidateLabel(ca.format)) + '</span></div>' +
      '<div class="bb-cand-status">' + esc(ca.technical_status) + '</div>' +
      '<div class="bb-cand-exp">' + esc(ca.explanation) + '</div></div>';
  }
  function specRow(k, v) { return '<div class="bb-sp-r"><span class="bb-sp-k">' + esc(k) + '</span><span class="bb-sp-v">' + v + '</span></div>'; }
  // Stage-2 action, shown ONLY for a Category-1 recommendation. Enabled when a token exists (resolved + a real
  // catalogue format). For an unresolved identity it is disabled with a prompt; for a resolved recommendation
  // that has no catalogue format to source (application-review / process-specific) it is omitted entirely.
  // Owner-authored, VERBATIM. Line 1 is the heading; lines 2-3 are the body. Group A and Group B are
  // deliberately different wording: A = no mapped commercial form exists for this cell; B = mapped forms
  // exist but none suits this application. B must NEVER read as "we just need to find another SKU", and
  // neither may reuse renderStage2Result's ask_us_to_source string (that one means the catalogue WAS checked).
  var STAGE2_FALLBACK = {
    enquiry_unmapped_requirement: ['Need help sourcing this specification?',
      'This application is outside the standard mapped formats.',
      'Ask Herbuno about sourcing the ingredient described above.'],
    enquiry_no_suitable_mapped_format: ['No standard mapped format fits this application.',
      'Use the ingredient specification above as the sourcing requirement.',
      'Ask Herbuno if you need help identifying or supplying a suitable material.'],
  };
  // Reuses the existing .bb-sourcing / .bb-actions classes and the existing [data-enquiry] handler — no new
  // CSS, no new modal, no new flow, no Stage-2 network call (so nothing here can imply the catalogue or
  // stock was checked). Cell-specific wording is NEVER hard-coded: Group B points back to Row 3.
  function stage2Fallback(lines) {
    return '<div class="bb-sourcing"><p class="bb-sourcing-msg"><strong>' + esc(lines[0]) + '</strong></p>' +
      '<p class="bb-sourcing-msg">' + esc(lines[1]) + '</p>' +
      '<p class="bb-sourcing-msg">' + esc(lines[2]) + '</p>' +
      '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Ask Herbuno</button></div></div>';
  }
  // `inBlend` suppresses ONLY the new fallback action. Blend rows reuse the single-mode renderers verbatim
  // (renderBlendBody -> renderResponse -> here), and Stage 2 is out of scope for blend mode. The pre-existing
  // token button is left exactly as it is today — changing it is not in this fix's scope.
  function renderStage2Action(resp, inBlend) {
    // G2 Option A: the inBlend check moved ABOVE the token branch. It previously guarded only the
    // fallback, so the token button leaked into blend rows — and submitSourcing() then read single-mode
    // S.resp, firing Stage 2 for a DIFFERENT ingredient. Blend mode declares sourcing out of scope
    // (see renderBlendBody's header), so no Stage-2 action renders in a blend row at all. Per-row Stage 2
    // would be a NEW blend capability (ing.proc + a render target) and is deliberately not in this patch.
    if (inBlend) return '';
    if (resp.specification_token) {
      return '<div class="bb-actions"><button class="bb-btn" data-sourcing="1">Check sourcing options →</button></div>';
    }
    if (resp.identity_status !== 'resolved') {
      return '<div class="bb-actions"><button class="bb-btn" disabled>Check sourcing options →</button>' +
        '<span class="bb-actions-note">Confirm the botanical identity to check sourcing.</span></div>';
    }
    // Category-1 recommendation with no selectable format: offer a truthful enquiry route rather than
    // ending the card silently. Any other category has nothing to source, so it correctly renders nothing.
    var lines = STAGE2_FALLBACK[resp.stage2_action];
    return lines ? stage2Fallback(lines) : '';
  }
  // "Pomegranate — Punica granatum"; if the display name is just the Latin (no common name), show it once.
  function whoLine(id) {
    if (!id.display_name) return '<i>unnamed</i>';
    var latin = id.authority_name && id.authority_name !== id.display_name ? ' — <i>' + esc(id.authority_name) + '</i>' : '';
    return esc(id.display_name) + latin;
  }
  // Cat-1 status subtype -> distinct visual class, so "Application review needed" / "Process-specific
  // recommendation" never read as certain as "Best physical fit".
  function statusSlug(s) {
    if (/^Best/.test(s)) return 'best';
    if (/^Conditional/.test(s)) return 'cond';
    if (/^Process/.test(s)) return 'process';
    return 'review';
  }
  // ---- ADR-015 seven-row contract: approved interim/empty strings (owner-authored — do NOT reword) ----
  var PLANT_PART_EMPTY = 'Plant-part guidance is not yet included in this version. Confirm the required plant part before requesting a supplier quotation.';
  var VERIFICATION_EMPTY = 'Verification guidance is not yet included in this version. Required supplier evidence and testing must be confirmed before purchase.';
  var NO_CHECKS = 'No additional formulation checks identified for this response.';
  // ADR-015 §6 THIRD STATE. reasoning_checks === null means the Worker did NOT evaluate the checks for this
  // response (every non-Cat-1 cell, and the 13 Cat-1 cells with no selectable format). It does NOT mean zero
  // checks were found: reasoningChecks() always returns all three, so "evaluated and none found" cannot
  // currently occur. NO_CHECKS stays reserved for that distinct state and must never render for a null.
  var CHECKS_NOT_EVALUATED = 'Formulation checks were not evaluated for this response.';
  var PRODUCT_PROVIDES = 'None — the product provides this.';
  // non-value strings for §2 qualifier suppression (cond 1 & 3); duck-typed .has() to avoid Set.
  var NON_VALUES = { has: function (s) { return s === PRODUCT_PROVIDES || s === PLANT_PART_EMPTY || s === VERIFICATION_EMPTY || s === NO_CHECKS || s === CHECKS_NOT_EVALUATED; } };

  function outcomeBadge(text) { return '<span class="bb-status bb-status-' + statusSlug(text) + '">' + esc(text) + '</span>'; } // text ALWAYS present (locked a11y rule)
  function checksCount(rc) { if (!rc) return 0; var n = 0; if (rc.phase) n++; if (rc.dissolution) n++; if (rc.process) n++; return n; }
  // Row 6 text. THREE states, never two (ADR-015 §6): not evaluated (null) / evaluated-none (0) / evaluated-some.
  // checksCount() keeps its 0-for-null contract; the null test happens here so the two states stay distinct.
  function checksLabel(rc) {
    if (rc == null) return CHECKS_NOT_EVALUATED;
    var n = checksCount(rc);
    return n ? (n + ' formulation check' + (n > 1 ? 's' : '')) : NO_CHECKS;
  }
  // Row 3 Ingredient to Source: message.body + §2 qualifier, or the product-provides non-applicability string.
  function row3Value(resp) {
    var m = resp.message;
    if (m.category === '2' || m.category === '4B') return esc(PRODUCT_PROVIDES);
    var v = esc(m.body);
    var q = formatQualifier(m.body, QUALIFIER_BY_CODE[resp.recommended_format], NON_VALUES);
    if (q) v += ' <i class="bb-qual">(' + esc(q) + ')</i>';
    return v;
  }
  // Clickable candidate list — each re-submits pinned to that species' Latin (authority) name. Names only,
  // never IDs/counts. The gate: rows 3-7 do NOT render until a species is chosen.
  function candidateChoices(resp) {
    var cands = resp.identity && resp.identity.candidates;
    if (!cands || !cands.length) return '';
    var items = cands.map(function (c) {
      var latin = c.authority_name ? '<i>' + esc(c.authority_name) + '</i>' : '';
      var common = c.display_name && c.display_name !== c.authority_name ? esc(c.display_name) : '';
      var label = common && latin ? common + ' — ' + latin : (latin || common || esc(c.display_name || 'unnamed'));
      var pick = c.authority_name || c.display_name || '';
      return '<li>' + label + ' <button class="bb-btn o bb-pick" data-select-candidate="' + esc(pick) + '">Use this</button></li>';
    }).join('');
    return '<div class="bb-card-cands"><p>This name may refer to more than one botanical — choose a species:</p><ul>' + items + '</ul></div>';
  }
  function copyDetailsBtn() { return '<div class="bb-actions"><button class="bb-btn o" data-copydetails="1">Copy details</button></div>'; }

  // V2 verdict block. Botanical Identity (governed field 1 of 5) is presented ONCE, as the verdict heading:
  // the display name as the title, the authority (Latin) name in italics beneath it. The literal label stays
  // visible so the five-field contract reads in order. Single mode carries Change here (the separate
  // "Ingredient" summary strip is then not rendered — see pillsFromRole); a blend row changes its botanical
  // from the blend controls, so no Change is rendered inside a blend result.
  function verdictBlock(id, inBlend) {
    var name = id.display_name || id.authority_name || 'unnamed';
    var latin = id.authority_name && id.authority_name !== name ? '<p class="bb-vb-sub"><i>' + esc(id.authority_name) + '</i></p>' : '';
    return '<div class="bb-vb"><div class="bb-vb-main"><span class="bb-sp-k bb-vh">Botanical Identity</span>' +
      '<p class="bb-vb-t">' + esc(name) + '</p>' + latin + '</div>' +
      (inBlend ? '' : '<button type="button" class="bb-change" data-wiz-change="botanical">Change</button>') + '</div>';
  }
  // ---- the invariant five-field card (RESOLVED only) ----
  // Order: verdict (Botanical Identity) · rows 2-5 · the buyer's own selection · the ONE primary action ·
  // the reasoning disclosures.
  function renderResolvedCard(resp, inBlend) {
    var m = resp.message;
    var outcome = m.technical_status || m.header;
    var rows =
      specRow('Ingredient to Source', row3Value(resp)) +
      specRow('Outcome', outcomeBadge(outcome)) +
      specRow('Why', esc(m.why)) +
      specRow('Formulation Checks', esc(checksLabel(resp.reasoning_checks)));
    // The primary CTA and the reasoning link share one row (inline >=601px, stacked <=600px — CSS) when the
    // primary is the Check-sourcing button. Any other Stage-2 action (enquiry panel, none) keeps them stacked.
    var action = renderStage2Action(resp, inBlend), why = reasoningDisclosure(resp);
    var cta = (!inBlend && resp.specification_token) ? '<div class="bb-cta-row">' + action + why + '</div>' : action + why;
    return verdictBlock(resp.identity || {}, inBlend) + '<div class="bb-card resolved">' + rows + '</div>' +
      renderCandidate(resp) + cta;
  }
  // ---- gated card: ambiguous / unrecognised — Row 1 + Row 2 only; rows 3-7 hidden until a species is chosen ----
  function renderGatedCard(resp) {
    if (resp.identity_status === 'ambiguous') {
      return '<div class="bb-card ambiguous">' +
        specRow('Botanical Identity', esc('Identity clarification required')) +
        candidateChoices(resp) + '</div>';
    }
    return '<div class="bb-card unrecognised">' +
      specRow('Botanical Identity', esc('No botanical identity matched this name')) +
      '<p class="bb-card-sub">Check the spelling or enter the botanical (Latin) name to continue.</p>' +
      '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Open an enquiry</button></div></div>';
  }
  // Copy Details ROWS (ADR §4.10): the seven-row lines only — NO context line. Shared by single mode
  // (copyText) and blend mode (copyBlendText). Empty states retained; qualifier inline with Ingredient;
  // NO ids/scores/matrix keys/category numbers.
  function copyRows(resp) {
    var L = [];
    if (resp.clarification_required) {
      L.push('Botanical Identity: Identity clarification required');
      if (resp.clarification_message) L.push(resp.clarification_message);
      return L.join('\n');
    }
    var s = resp.identity_status;
    if (s === 'resolved') {
      var m = resp.message, id = resp.identity || {};
      var idline = id.display_name ? (id.display_name + (id.authority_name && id.authority_name !== id.display_name ? ' — ' + id.authority_name : '')) : (id.authority_name || 'unnamed');
      var r3;
      if (m.category === '2' || m.category === '4B') { r3 = PRODUCT_PROVIDES; }
      else { r3 = m.body; var q = formatQualifier(m.body, QUALIFIER_BY_CODE[resp.recommended_format], NON_VALUES); if (q) r3 += ' (' + q + ')'; }
      L.push('Botanical Identity: ' + idline);
      L.push('Ingredient to Source: ' + r3);
      L.push('Outcome: ' + (m.technical_status || m.header));
      L.push('Why: ' + (m.why || ''));
      L.push('Formulation Checks: ' + checksLabel(resp.reasoning_checks));
      if (resp.reasoning_checks) {
        var rc = resp.reasoning_checks;
        if (rc.phase) L.push('  - Phase compatibility: ' + rc.phase);
        if (rc.dissolution) L.push('  - Dissolution / dispersion: ' + rc.dissolution);
        if (rc.process) L.push('  - Process constraint: ' + rc.process);
      }
      return L.join('\n');
    }
    if (s === 'ambiguous') {
      L.push('Botanical Identity: Identity clarification required');
      var cands = (resp.identity && resp.identity.candidates) || [];
      if (cands.length) { L.push('  Candidates:'); cands.forEach(function (c) { L.push('    - ' + ((c.display_name && c.display_name !== c.authority_name) ? (c.display_name + ' — ' + (c.authority_name || '')) : (c.authority_name || c.display_name || 'unnamed'))); }); }
    } else {
      L.push('Botanical Identity: No botanical identity matched this name');
    }
    return L.join('\n');
  }
  function copyText(resp, ctx) {
    return 'Context: ' + (ctx.botanical || '') + ' · ' + productName(ctx.product) + ' · ' + roleLabel(ctx.role) + '\n\n' + copyRows(resp);
  }

  // ================= BLEND MODE (minimal; client-only, Stage-1 reused per ingredient) =================
  // Each ingredient is an INDEPENDENT Stage-1 call; results stack as their own card via the EXACT single-mode
  // renderers, verbatim. No cross-ingredient analysis, no sourcing/inventory (out of scope). Row identity is a
  // stable client id (never index/name/role). Rendering walks ingredients[] so output order == input order.
  var BLEND_MIN = 2, BLEND_MAX = 10, BLEND_CAP = 4;
  function renderBlendBody(ing) {
    if (ing.status === 'loading' || ing.status === 'pending') return renderLoading('Checking ' + esc(ing.botanical || 'ingredient') + '…');
    if (ing.status === 'ratelimited') return renderRateLimited(false); // blend row: NO link until the G2 modal-state fix
    if (ing.status === 'notavailable') return renderNotAvailable(ing.message);
    if (ing.status === 'degraded') return renderDegraded(ing.message);
    if (ing.status === 'done' && ing.resp) return renderResponse(ing.resp, true); // verbatim: seven-row OR gated
    return '<p class="bb-empty">Not run yet.</p>';
  }
  // An ingredient's number is its POSITION in the blend, everywhere: control summaries, result headers and
  // Copy all read it from here, so re-answering ingredient 1 can never renumber the others.
  // Only ingredients that have been asked (status other than idle) get a result card: an in-progress row is
  // shown, and controlled, in the control area and never as a stranded "Not run yet" card.
  // The outcome an ingredient's summary line shows — the SAME text its expanded result shows (the Outcome
  // badge for a resolved row, otherwise that state's existing card heading). No new copy.
  function blendOutcomeHtml(ing) {
    if (ing.status === 'loading' || ing.status === 'pending') return esc('Checking ' + (ing.botanical || 'ingredient') + '…');
    if (ing.status === 'ratelimited') return esc('Too many checks just now');
    if (ing.status === 'notavailable') return esc('Not available for this product');
    if (ing.status === 'degraded') return esc('Temporarily unavailable');
    var r = ing.resp; if (!r) return '';
    if (r.clarification_required) return esc('Identity clarification required');
    if (r.guidance_status) return esc('Not available for this product');
    if (r.identity_status === 'resolved') return outcomeBadge(r.message.technical_status || r.message.header);
    if (r.identity_status === 'ambiguous') return esc('Identity clarification required');
    return esc('No botanical identity matched this name');
  }
  function blendSourceHtml(ing) {
    var r = ing.resp;
    return (ing.status === 'done' && r && r.identity_status === 'resolved' && !r.guidance_status && !r.clarification_required) ? row3Value(r) : '';
  }
  // BLEND RESULT. A blend-level summary (one line per ingredient: ingredient · role · ingredient to source ·
  // Outcome) sits above the per-ingredient results. Each result is a collapsible <details>; only the most
  // recently added ingredient is open by default. A buyer's own open/close is held in memory on the row
  // (ing.uiOpen) and is never stored — a reload returns to the landing as before.
  function renderBlend(blend) {
    if (!blend || !blend.ingredients.length) return '';
    if (!blend.ingredients.some(function (ing) { return ing.status !== 'idle'; })) return '<p class="bb-empty">Each ingredient’s result appears here as you complete it.</p>';
    var latest = null;
    blend.ingredients.forEach(function (ing) { if (ing.status !== 'idle') latest = ing; });
    var summary = '', cards = '';
    blend.ingredients.forEach(function (ing, idx) {
      if (ing.status === 'idle') return;
      var n = idx + 1, open = (typeof ing.uiOpen === 'boolean') ? ing.uiOpen : ing === latest;
      summary += '<div class="bb-bs-r"><span class="bb-bs-n">Ingredient ' + n + '</span>' +
        '<span class="bb-bs-l">' + esc(blendRowLabel(ing)) + '</span>' +
        '<span class="bb-bs-src">' + blendSourceHtml(ing) + '</span>' +
        '<span class="bb-bs-o">' + blendOutcomeHtml(ing) + '</span></div>';
      cards += '<details class="bb-blend-ing" data-blend-row="' + esc(ing.id) + '"' + (open ? ' open' : '') + '>' +
        '<summary class="bb-blend-h">Ingredient ' + n + ': ' + esc(blendRowLabel(ing)) + '</summary>' +
        '<div class="bb-bi-body">' + renderBlendBody(ing) + '</div></details>';
    });
    return '<div class="bb-blend-results"><div class="bb-blend-sum">' + summary + '</div>' + cards +
      '<div class="bb-actions"><button class="bb-btn o" data-copyblend="1">Copy details</button></div></div>';
  }
  // Locked blend Copy format: Blend Product + per-ingredient header + seven-row lines (or governed gate/error
  // text for unresolved rows — never assumes a successful resp). No ids/keys/tokens/metadata; no conclusion.
  function copyBlendText(blend, productLabel) {
    var L = ['Blend Product: ' + productLabel, ''];
    blend.ingredients.forEach(function (ing, idx) {
      L.push('Ingredient ' + (idx + 1) + ': ' + blendRowLabel(ing));
      if (ing.status === 'done' && ing.resp) L.push(copyRows(ing.resp));
      else if (ing.status === 'notavailable') L.push(notAvailableMessage(ing.message));
      else if (ing.status === 'ratelimited') L.push('Too many checks just now — this ingredient was not completed.');
      else if (ing.status === 'degraded') L.push(ing.message || DEGRADED_FALLBACK);
      else L.push('[not run]');
      L.push('');
    });
    return L.join('\n').replace(/\n+$/, '');
  }
  // The one row the buyer is answering: the first that has not produced a response.
  function blendActiveRowOf(blend) {
    for (var i = 0; i < blend.ingredients.length; i++) if (blend.ingredients[i].status !== 'done') return blend.ingredients[i];
    return null;
  }
  // PILLS-FIRST BLEND. The product is answered once and shown as shared context. Ingredients are listed in
  // blend order under their position number. A completed ingredient collapses to one line; exactly ONE row
  // is active and runs the identical Q2 -> Q3 -> Q4 sequence a single-ingredient journey runs, through the
  // same pure renderer. Any OTHER unfinished row (e.g. ingredient 3 half-answered when the buyer pressed
  // Change on ingredient 1) is PRESERVED: it keeps its answers, stays listed with Remove as the explicit
  // way to discard it, and becomes the active row again once the rows before it are complete.
  function blendControlsHtml(b) {
    var active = blendActiveRowOf(b);
    // No step numeral on the locked product: it is the shared formulation context, not step 1 of the
    // ingredient sequence. Numbering belongs to the ingredients, which are 1..n unambiguously.
    var H = '<div class="bb-wiz">' +
      '<div class="bb-sum"><span class="bb-sum-k">Making</span>' +
        '<span class="bb-sum-v">' + esc(productName(b.product)) + '</span>' +
        '<button type="button" class="bb-change" data-wiz-restart="1">Start over</button></div>';
    b.ingredients.forEach(function (ing, idx) {
      var n = idx + 1;
      var rm = (b.ingredients.length <= BLEND_MIN) ? '' :
        '<button type="button" class="bb-change" data-ing-remove="' + esc(ing.id) + '">Remove</button>';
      if (ing === active) {
        H += '<p class="bb-q-hint" style="margin-top:14px">Ingredient ' + n +
          ' of this ' + esc(productName(b.product)) + '.</p>';
        H += pillsControls(ing, { hideProduct: true });
        return;
      }
      if (ing.status === 'done') {
        // Change reopens THIS ingredient at Q2; Remove drops it. A completed blend ingredient keeps the
        // same affordances a completed step has in single mode.
        H += '<div class="bb-sum" data-blend-row="' + esc(ing.id) + '">' +
          '<span class="bb-sum-n">' + n + '</span><span class="bb-sum-k">Ingredient</span>' +
          '<span class="bb-sum-v">' + esc(blendRowLabel(ing)) + '</span>' +
          '<button type="button" class="bb-change" data-ing-change="' + esc(ing.id) + '">Change</button>' + rm + '</div>';
        return;
      }
      // preserved, not yet active
      H += '<div class="bb-sum bb-sum-wait" data-blend-row="' + esc(ing.id) + '">' +
        '<span class="bb-sum-n">' + n + '</span><span class="bb-sum-k">Ingredient</span>' +
        '<span class="bb-sum-v">' + esc(ing.role ? blendRowLabel(ing) : 'Not started') + ' · continues after ingredient ' +
          (b.ingredients.indexOf(active) + 1) + '</span>' + rm + '</div>';
    });
    if (!active) {
      H += '<div class="bb-actions bb-addmore"><button type="button" class="bb-btn o" data-wiz-addmore="1"' +
        (b.ingredients.length >= BLEND_MAX ? ' disabled' : '') + '>+ Add another ingredient</button>' +
        '<span class="bb-opt">Keeps ' + esc(productName(b.product)) + ' as the formulation.</span></div>';
    }
    return H + '</div>';
  }
  // The blend itself is not persisted, so a reload can only return to build mode if there is a blend to
  // return to. Without a product it falls back to the single journey (a stored 'build' alone once left a
  // "Making: (blank)" surface whose role pills did nothing).
  function restoreMode(stored, blend) {
    return (stored === 'build' && blend && blend.product) ? 'build' : 'quick';
  }
  // Generic concurrency-capped pool: at most `cap` worker(item) promises in flight; resolves when all settle.
  // Each worker owns its errors, so one rejection never stalls the pool.
  function runPool(items, cap, worker) {
    return new Promise(function (resolve) {
      var i = 0, active = 0, done = 0, n = items.length;
      if (!n) return resolve();
      function fin() { active--; done++; if (done === n) resolve(); else step(); }
      // launch(it) binds the item PER CALL (a function argument) — a `var it` in the loop is function-scoped
      // and would be reassigned before the deferred callbacks fire, so every worker got the last item.
      function launch(it) { Promise.resolve().then(function () { return worker(it); }).then(fin, fin); }
      function step() { while (active < cap && i < n) { active++; launch(items[i++]); } }
      step();
    });
  }
  // A 200 with guidance_status is a domain outcome (product×role isn't a supported cell) — render the
  // guidance card, whatever identity_status says.
  function renderGuidanceCard(resp) {
    return '<div class="bb-card notavailable"><div class="bb-card-head">Not available for this product</div>' +
      '<p class="bb-card-msg">' + esc(resp.guidance || 'This combination is not available.') + '</p>' +
      '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Open an enquiry</button></div></div>';
  }
  // Temporary ADR-013 identity safeguard (ADR-015 launch-track ruling): a known identity defect returns an
  // identity-clarification prompt — Row 1 clarification state ONLY, NO rows 3–7. The message is owner-authored
  // and sent by the Worker; it is NEVER reworded client-side.
  function renderClarificationCard(resp) {
    var msg = resp.clarification_message || 'Botanical identity clarification is required before a recommendation can be generated.';
    return '<div class="bb-card ambiguous"><div class="bb-card-head">Identity clarification required</div>' +
      '<p class="bb-card-msg">' + esc(msg) + '</p>' +
      '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Open an enquiry</button></div></div>';
  }
  // Valid 200 responses (the caller must have already confirmed apiCompatible).
  function renderResponse(resp, inBlend) {
    if (resp.clarification_required) return renderClarificationCard(resp);
    if (resp.guidance_status) return renderGuidanceCard(resp);
    if (resp.identity_status === 'resolved') return renderResolvedCard(resp, inBlend); // full seven rows
    return renderGatedCard(resp);                                             // ambiguous / unrecognised — rows 3-7 gated
  }
  function renderStage2Result(proc) {
    // Stage 2 is a temporary proxy over the observed-form graph (common commercial formats, trade-convention
    // derived) — NOT Herbuno inventory. Copy must NEVER imply the catalogue/stock was checked (real-stock
    // integration is an open ADR-014 gap): frame around what forms this botanical is commonly supplied in.
    var cls = proc.match_class, msg;
    if (cls === 'exact_match') msg = 'This is a common commercial format for this botanical — send Herbuno an enquiry to source it.';
    else if (cls === 'compatible_alternative') msg = 'This botanical is commonly supplied in related formats; the exact form may need a sourcing request.';
    else msg = 'This isn’t among the common commercial formats mapped for this botanical — ask Herbuno to source it.';
    return '<div class="bb-sourcing"><div class="bb-sourcing-h">Sourcing options</div>' +
      '<p class="bb-sourcing-msg">' + esc(msg) + '</p>' +
      '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Ask Herbuno to source this →</button>' +
      '<button class="bb-btn o" data-backspec="1">← Back to specification</button></div></div>';
  }
  // 400 from the guided flow means the chosen product×role combination isn't in the matrix (or an input was
  // rejected) — a mapping gap, NOT an infrastructure failure, so it must not read as "degraded".
  function notAvailableMessage(detail) {
    if (detail === 'unknown_product_role') return "That role isn't set up for this product. Try a different role, or open an enquiry.";
    return 'Please check your selections and try again.';
  }
  function renderNotAvailable(detail) { return '<div class="bb-card notavailable"><div class="bb-card-head">Not available for this product</div>' +
    '<p class="bb-card-msg">' + esc(notAvailableMessage(detail)) + '</p>' +
    '<div class="bb-actions"><button class="bb-btn o" data-enquiry="1">Open an enquiry</button></div></div>'; }
  function renderLoading(label) { return '<div class="bb-loading"><span class="bb-spinner" aria-hidden="true"></span>' + esc(label || 'Generating specification…') + '</div>'; }
  // G4 item 3 — ONE honest message for ALL NINE block reasons. The limiter's windows run from 60s to 24h
  // (rate_limiter_do.js MIN/HOUR/DAY), so the previous "Please wait a moment and try again." was wrong for
  // SEVEN of the nine and read to a buyer as a malfunction: they waited, retried, and were still blocked.
  // This wording therefore promises NO specific window. A per-window-accurate variant would require the
  // Worker to send the block reason — a response-shape change touching API_SCHEMA_VERSION 3 — and is
  // explicitly OUT of the launch window. Owner-authored copy: do NOT reword.
  // G4 item 4 — "tell us what you're formulating" opens the EXISTING enquiry modal through the delegated
  // [data-enquiry] click handler. No new UI, no new flow. SINGLE MODE ONLY: blend rows get the same
  // message with NO link, because enquiryText() reads single-mode state (S.product/S.role/S.botanical)
  // while a blend's selections live in S.blend — a blend enquiry would send empty Product/Role/Botanical
  // lines. Enabling the link for blend rows is part of the G2 modal-state fix, NOT this PR.
  var RATE_MSG_HEAD = 'You\'ve made a lot of checks recently. Access will reset automatically. If you\'d like help sooner, ';
  var RATE_MSG_LINK = 'tell us what you\'re formulating';
  var RATE_MSG_TAIL = ' and we\'ll answer directly.';
  // Inline style rather than a class: the enquiry trigger sits mid-sentence, and blend-builder.css is a
  // separate deployable this change deliberately does not touch.
  var RATE_LINK_STYLE = 'background:none;border:0;padding:0;font:inherit;color:inherit;text-decoration:underline;cursor:pointer';
  function renderRateLimited(withEnquiryLink) {
    var mid = withEnquiryLink
      ? '<button type="button" data-enquiry="1" style="' + RATE_LINK_STYLE + '">' + esc(RATE_MSG_LINK) + '</button>'
      : esc(RATE_MSG_LINK);
    return '<div class="bb-card ratelimited"><div class="bb-card-head">Too many checks just now</div>' +
      '<p class="bb-card-msg">' + esc(RATE_MSG_HEAD) + mid + esc(RATE_MSG_TAIL) + '</p></div>';
  }
  function renderDegraded(message) { return '<div class="bb-card degraded"><div class="bb-card-head">Temporarily unavailable</div>' +
    '<p class="bb-card-msg">' + esc(message || DEGRADED_FALLBACK) + '</p>' +
    '<div class="bb-actions"><button class="bb-btn" data-retry="1">Retry</button>' +
    '<button class="bb-btn o" data-copysel="1">Copy my selections</button>' +
    '<button class="bb-btn o" data-enquiry="1">Open an enquiry</button></div></div>'; }

  // ================= PILLS-FIRST PROGRESSIVE DECISION SHELL =================
  // An INTERACTION LAYER over the unchanged governed flow. It asks one question at a time, collapses each
  // answered question to a one-line summary with Change, and reveals the next necessary question only.
  // It adds NO decision data: families/products/roles/candidate codes are the vocabularies the client
  // already shipped, and every outcome still comes from the Worker.
  //
  // Coverage stays reactive and Worker-authoritative (Ruling 2). The client never holds, caches or derives
  // a coverage map: after Q2 it ASKS, on the ordinary spec route, and the Worker answers. An uncovered pair
  // returns the governed not_available_for_product card and the journey stops there — neither Q3 nor Q4 is
  // produced. A covered pair returns a bare "continue" flag carrying no matrix content.
  var WIZ_STEPS = ['family', 'product', 'role', 'botanical'];
  function wizFamilies() { return PRODUCTS.map(function (g) { return g.fam; }); }
  function wizFamilyOf(pid) { for (var i = 0; i < PRODUCTS.length; i++) { var it = PRODUCTS[i].items;
    for (var j = 0; j < it.length; j++) if (it[j].id === pid) return PRODUCTS[i].fam; } return ''; }
  function wizItems(fam) { for (var i = 0; i < PRODUCTS.length; i++) if (PRODUCTS[i].fam === fam) return PRODUCTS[i].items; return []; }
  // Changing an answer MUST invalidate every dependent answer, the result and any in-flight response.
  // S.gen is bumped so a late reply from a discarded question can never paint a stale card.
  function newWizState() { return { mode: 'quick', product: '', role: '', botanical: '', candidate: '', view: 'input',
    message: '', resp: null, proc: null, wiz: { step: 'family', family: '', formOpen: false, cov: '' }, gen: 0,
    blend: { product: '', ingredients: [], nextId: 1, seq: 0, running: false } }; }
  function wizInvalidate(st, from) {
    var i = WIZ_STEPS.indexOf(from);
    if (i <= 0) { st.wiz.family = ''; st.product = ''; }
    if (i <= 1) { st.product = ''; }
    if (i <= 2) { st.role = ''; }
    if (i <= 3) { st.botanical = ''; }
    st.candidate = ''; st.resp = null; st.proc = null; st.message = ''; st.view = 'input';
    st.wiz.cov = '';                       // a changed product or role invalidates any coverage verdict
    st.gen = (st.gen || 0) + 1;            // ...and no in-flight preflight may land against the new journey
    st.wiz.step = from;
  }
  function wizPill(label, attrs, on, sub, desc) {
    return '<button type="button" class="bb-pill' + (on ? ' on' : '') + '" ' + attrs + '>' + esc(label) +
      (desc ? '<span class="bb-pill-d">' + esc(desc) + '</span>' : '') +
      (sub ? '<span class="bb-pill-s">' + esc(sub) + '</span>' : '') + '</button>';
  }
  function wizFamilyDesc(fam) { for (var i = 0; i < PRODUCTS.length; i++) if (PRODUCTS[i].fam === fam) return PRODUCTS[i].desc || ''; return ''; }
  // n is null inside a blend row: there the numerals belong to the ingredients, so the row's own steps
  // carry none (a step "2" beside Ingredient 2 read as the same sequence).
  function wizSummary(n, k, v, changeStep) {
    return '<div class="bb-sum">' + (n == null ? '' : '<span class="bb-sum-n">' + n + '</span>') +
      '<span class="bb-sum-k">' + esc(k) + '</span>' +
      '<span class="bb-sum-v">' + esc(v) + '</span>' +
      '<button type="button" class="bb-change" data-wiz-change="' + esc(changeStep) + '">Change</button></div>';
  }
  function wizQuestion(n, title, bodyHtml, hint) {
    return '<div class="bb-q"><p class="bb-q-h">' + (n == null ? '' : '<span class="bb-q-n">' + n + '</span>') + esc(title) + '</p>' +
      (hint ? '<p class="bb-q-hint">' + esc(hint) + '</p>' : '') + bodyHtml + '</div>';
  }
  // Q4 is a REFINEMENT on an existing recommendation, not a gate before it: the engine answers
  // "is my proposed format right?" against a cell it has already evaluated. It is therefore offered after
  // the result, only when the identity resolved, and it is always skippable.
  function wizFormStep(st, noNum, n) {
    n = noNum ? null : (n || '4');
    if (!st.resp || st.resp.identity_status !== 'resolved' || st.resp.guidance_status) return '';
    if (st.candidate) return wizSummary(n, 'Form you had in mind', candidateLabel(st.candidate === 'OTHER' ? null : st.candidate), 'form');
    if (!st.wiz.formOpen) return formOpenBtn();
    var pills = CANDIDATE_GROUPS.map(function (g) {
      return '<p class="bb-pill-g">' + esc(g.label) + '</p><div class="bb-pills">' + g.codes.map(function (c) {
        return wizPill(candidateLabel(c), 'data-wiz-form="' + esc(c) + '"', false);
      }).join('') + '</div>';
    }).join('');
    return wizQuestion(n, 'Do you have a form in mind?', pills +
      '<div class="bb-actions"><button type="button" class="bb-btn o" data-wiz-skipform="1">Skip — no preference</button></div>',
      'Optional. We will tell you whether it fits this product and role.');
  }
  // opts.hideProduct — a blend row does not ask for the product: it is the shared formulation context,
  // rendered once above the rows. Everything else about the row's journey is identical to single mode,
  // which is the point: adding an ingredient must not change the interaction paradigm. A blend row's
  // steps are unnumbered, because the numerals in a blend belong to the ingredients.
  function pillsControls(st, opts) {
    opts = opts || {};
    var H = '<div class="bb-wiz">';
    if (opts.hideProduct) return pillsFromRole(st, H, true);
    // ---- Q1 product, via family ----
    if (st.product) H += wizSummary('1', 'Making', productName(st.product), 'family');
    else if (st.wiz.step === 'product' && st.wiz.family) {
      H += wizSummary('1', 'Category', st.wiz.family, 'family');
      H += wizQuestion('2', 'Which one exactly?', '<div class="bb-pills">' + wizItems(st.wiz.family).map(function (it) {
        return wizPill(it.name, 'data-wiz-product="' + esc(it.id) + '"', false);
      }).join('') + '</div>');
      return H + '</div>';
    } else {
      H += wizQuestion('1', 'What are you formulating?', '<div class="bb-pills bb-pills-fam">' + wizFamilies().map(function (f) {
        return wizPill(f, 'data-wiz-family="' + esc(f) + '"', false, wizItems(f).length + ' products', wizFamilyDesc(f));
      }).join('') + '</div>', 'Pick a category, then the exact product.');
      return H + '</div>';
    }
    return pillsFromRole(st, H);
  }
  // Q2 onward — shared verbatim by single mode and by every blend row.
  function pillsFromRole(st, H, noNum) {
    // ---- Q2 role ----
    if (st.role) H += wizSummary(noNum ? null : '2', 'Role', roleLabel(st.role), 'role');
    else {
      H += wizQuestion(noNum ? null : '2', 'What role will this ingredient play?', '<div class="bb-pills">' + ROLES.map(function (r) {
        return wizPill(r.label, 'data-wiz-role="' + esc(r.id) + '"', false, r.desc);
      }).join('') + '</div>');
      return H + '</div>';
    }
    // ---- coverage verdict (Worker-authoritative; never cached, never derived here) ----
    // UNCOVERED stops the journey at Q2: the governed not_available card renders in the results area and
    // neither Q3 nor Q4 is ever produced. COVERED is a bare "continue" signal and nothing more.
    if (st.wiz.cov === 'uncovered') return H + '</div>';
    if (st.wiz.cov === 'pending') return H + '<div class="bb-q"><p class="bb-q-hint">Checking whether HerbIQ covers this combination…</p></div></div>';
    if (st.wiz.cov !== 'covered') return H + '</div>';
    // ---- Q3 botanical ----
    // Single mode, resolved result: the verdict block names the botanical and carries its Change, so the
    // summary strip would only repeat it (V2: identity shown once).
    var verdictNamesIt = !noNum && st.resp && st.resp.identity_status === 'resolved' &&
      !st.resp.guidance_status && !st.resp.clarification_required;
    if (st.botanical && (st.resp || st.view === 'loading')) {
      if (!verdictNamesIt) H += wizSummary(noNum ? null : '3', 'Ingredient', st.botanical, 'botanical');
    } else {
      H += wizQuestion(noNum ? null : '3', 'Which botanical or ingredient?',
        '<input class="bb-in" id="bb-bot" value="' + esc(st.botanical) + '" placeholder="Type any botanical — e.g. Ashwagandha" autocomplete="off">' +
        '<div class="bb-actions"><button class="bb-btn" id="bb-go"' + ((st.botanical || '').trim() ? '' : ' disabled') + '>Get specification →</button></div>',
        'Type the common or botanical name. We resolve it against the Herbuno identity backbone.');
      return H + '</div>';
    }
    if (noNum) H += wizFormStep(st, true);   // single mode: the form step lives in the result action stack
    return H + '</div>';
  }
  function formOpenBtn() { return '<button type="button" class="bb-btn o" data-wiz-openform="1">Have a form in mind?</button>'; }
  // V2 single-ingredient result, top to bottom: verdict (Botanical Identity) · rows · [your selection] ·
  // 1 Check sourcing options · 2 How this result was reasoned · 3 Have a form in mind? · 4 Copy details ·
  // 5 + Add another ingredient. When the form question is open, or a form has been chosen, it renders at
  // position 3 (above the secondary row) instead of the button.
  // `after` (optional) is inserted directly beneath the response — the Stage-2 sourcing result in the sourcing view.
  function singleResultHtml(st, after) {
    var resp = st.resp; if (!resp) return '';
    var formable = resp.identity_status === 'resolved' && !resp.guidance_status && !resp.clarification_required;
    var formBlock = formable && (st.candidate || st.wiz.formOpen) ? wizFormStep(st, false, '3') : '';
    var copy = (!resp.guidance_status && !resp.clarification_required) ? copyDetailsBtn() : '';
    var row = (formable && !st.candidate && !st.wiz.formOpen ? formOpenBtn() : '') + copy + addAnotherBtn(st);
    var inner = renderResponse(resp) + (after || '') + formBlock + (row ? '<div class="bb-acts2">' + row + '</div>' : '');
    // Resolved verdicts get the layout wrapper: one column below 990px in exactly this DOM order; at >=990px
    // CSS moves the CTA and the secondary actions into a sticky right rail. DOM order is never changed.
    return formable ? '<div class="bb-verdict' + (resp.specification_token ? ' has-cta' : '') + '">' + inner + '</div>' : inner;
  }
  function addAnotherBtn(st) {
    if (!st.resp || st.resp.identity_status !== 'resolved' || st.resp.guidance_status) return '';
    return '<div class="bb-actions bb-addmore"><button type="button" class="bb-btn o" data-wiz-addmore="1">+ Add another ingredient</button>' +
      '<span class="bb-opt">Keeps ' + esc(productName(st.product)) + ' as the formulation.</span></div>';
  }

  // ================= Browser bootstrap (skipped under Node) =================
  if (!IS_BROWSER) {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = { buildSpecBody: buildSpecBody, apiCompatible: apiCompatible, renderResponse: renderResponse,
        renderResolvedCard: renderResolvedCard, renderGatedCard: renderGatedCard, candidateChoices: candidateChoices,
        row3Value: row3Value, checksCount: checksCount, copyText: copyText, copyRows: copyRows, outcomeBadge: outcomeBadge,
        renderBlend: renderBlend, renderBlendBody: renderBlendBody, copyBlendText: copyBlendText, runPool: runPool,
        BLEND_MIN: BLEND_MIN, BLEND_MAX: BLEND_MAX, BLEND_CAP: BLEND_CAP,
        statusSlug: statusSlug, renderReasoning: renderReasoning,
        renderCandidate: renderCandidate, renderStage2Action: renderStage2Action, renderStage2Result: renderStage2Result,
        renderLoading: renderLoading, renderRateLimited: renderRateLimited, renderDegraded: renderDegraded,
        renderNotAvailable: renderNotAvailable, notAvailableMessage: notAvailableMessage, renderGuidanceCard: renderGuidanceCard,
        renderClarificationCard: renderClarificationCard, formatQualifier: formatQualifier, MATERIALLY_INFORMATIVE: MATERIALLY_INFORMATIVE,
        QUALIFIER_BY_CODE: QUALIFIER_BY_CODE, PLANT_PART_EMPTY: PLANT_PART_EMPTY, VERIFICATION_EMPTY: VERIFICATION_EMPTY, NO_CHECKS: NO_CHECKS,
        CHECKS_NOT_EVALUATED: CHECKS_NOT_EVALUATED, checksLabel: checksLabel,
        PRODUCTS: PRODUCTS, pillsControls: pillsControls, wizInvalidate: wizInvalidate, wizFamilyOf: wizFamilyOf,
        wizItems: wizItems, wizFamilies: wizFamilies, addAnotherBtn: addAnotherBtn, newWizState: newWizState,
        buildPreflightBody: buildPreflightBody, pillsFromRole: pillsFromRole, blendNewRow: blendNewRow,
        blendRowLabel: blendRowLabel, blendActiveRowOf: blendActiveRowOf, blendControlsHtml: blendControlsHtml,
        restoreMode: restoreMode, singleResultHtml: singleResultHtml, reasoningDisclosure: reasoningDisclosure,
        blendOutcomeHtml: blendOutcomeHtml, wizFamilyDesc: wizFamilyDesc,
        CANDIDATE_OPTIONS: CANDIDATE_OPTIONS,
        formLabel: formLabel, candidateLabel: candidateLabel, enquiryLines: enquiryLines, FORMAT_LABELS: FORMAT_LABELS,
        blendRowContext: blendRowContext, singleContext: singleContext, copySelectionText: copySelectionText,
        CANDIDATE_OPTIONS: CANDIDATE_OPTIONS, CANDIDATE_GROUPS: CANDIDATE_GROUPS, candidateOptions: candidateOptions,
        ROLES: ROLES, PRODUCTS: PRODUCTS, API_SCHEMA_VERSION: API_SCHEMA_VERSION,
        BUILD_STUB: BUILD_STUB };
    }
    return;
  }

  var root = document.getElementById('bb');
  if (!root) return;
  var S = { mode: 'quick', product: '', role: '', botanical: '', candidate: '', view: 'input', message: '', resp: null, proc: null,
    wiz: { step: 'family', family: '', formOpen: false, cov: '' }, gen: 0,
    blend: { product: '', ingredients: [], nextId: 1, seq: 0, running: false } };
  try { var lm = sessionStorage.getItem('hb_mode'); S.mode = restoreMode(lm, S.blend);
    if (lm === 'build' && S.mode !== 'build') sessionStorage.setItem('hb_mode', S.mode); } catch (e) {}
  // Stable client row id (NEVER index/name/role) — responses attach by id so removing/reordering never
  // misattaches. token = per-run generation guard; a late response with a stale token/removed row is dropped.
  // A blend row IS a wizard state: same fields pillsControls() reads, plus the blend-specific
  // status/resp/token the runner already used. product mirrors the shared formulation context so
  // pillsControls sees it as answered and starts the row at Q2.
  function blendNewRow() { return { id: 'r' + (S.blend.nextId++), product: S.blend.product || '', role: '', botanical: '',
    candidate: '', status: 'idle', resp: null, message: '', token: 0,
    wiz: { step: 'role', family: '', formOpen: false, cov: '' }, gen: 0 }; }
  // How an ingredient names itself. An uncovered row never had a botanical requested, so it must not
  // render an em-dash as though data were missing: it reports the governed outcome instead.
  function blendRowLabel(ing) {
    if (ing && ing.wiz && ing.wiz.cov === 'uncovered') return roleLabel(ing.role) + ' · not available';
    return (ing && ing.botanical ? ing.botanical : '') + (ing && ing.botanical ? ' · ' : '') + roleLabel(ing && ing.role);
  }
  // The one row the buyer is answering: the first that has not produced a response.
  function blendActiveRow() { return blendActiveRowOf(S.blend); }
  // Wizard events act on the active blend row in build mode, and on S in single mode.
  function wizTarget() { return S.mode === 'build' ? (blendActiveRow() || S) : S; }
  if (!S.blend.ingredients.length) S.blend.ingredients.push(blendNewRow(), blendNewRow()); // start at BLEND_MIN

  function productOptions() {
    var o = '<option value="">Select a product…</option>';
    for (var i = 0; i < PRODUCTS.length; i++) { o += '<optgroup label="' + esc(PRODUCTS[i].fam) + '">';
      var it = PRODUCTS[i].items; for (var j = 0; j < it.length; j++) o += '<option value="' + esc(it[j].id) + '"' + (it[j].id === S.product ? ' selected' : '') + '>' + esc(it[j].name) + '</option>';
      o += '</optgroup>'; }
    return o;
  }
  function roleOptions() {
    var o = '<option value="">Select a role…</option>';
    for (var i = 0; i < ROLES.length; i++) o += '<option value="' + esc(ROLES[i].id) + '"' + (ROLES[i].id === S.role ? ' selected' : '') + '>' + esc(ROLES[i].label) + ' — ' + esc(ROLES[i].desc) + '</option>';
    return o;
  }
  // ONE builder for both pickers (single mode + blend row) so the two can never drift. Emits the two
  // CANDIDATE_GROUPS as <optgroup>s, then 'Other' UNGROUPED at the end: it is neither a form nor a
  // specification, and trailing it reads as the escape hatch it is. 'Other' selects free text (never a
  // code sent to the server); an empty candidate is sent as nothing. `placeholder` is the empty-value
  // first option, which differs between the two surfaces.
  function candidateOptions(sel, placeholder) {
    var byCode = {}, other = null, i, j, c;
    for (i = 0; i < CANDIDATE_OPTIONS.length; i++) { c = CANDIDATE_OPTIONS[i]; if (c.code == null) other = c; else byCode[c.code] = c; }
    var o = '<option value="">' + esc(placeholder) + '</option>';
    for (i = 0; i < CANDIDATE_GROUPS.length; i++) {
      o += '<optgroup label="' + esc(CANDIDATE_GROUPS[i].label) + '">';
      for (j = 0; j < CANDIDATE_GROUPS[i].codes.length; j++) {
        c = byCode[CANDIDATE_GROUPS[i].codes[j]];
        if (!c) continue;                                   // unknown code in a group is skipped, never invented
        o += '<option value="' + esc(c.code) + '"' + (c.code === sel ? ' selected' : '') + '>' + esc(c.label) + '</option>';
      }
      o += '</optgroup>';
    }
    if (other) o += '<option value="OTHER"' + (sel === 'OTHER' ? ' selected' : '') + '>' + esc(other.label) + '</option>';
    return o;
  }
  function candidateOptionsHtml() {
    return candidateOptions(S.candidate, 'Not sure / no preference');
  }
  // ---- blend option builders (take the selected value; used per ingredient row) ----
  function optProducts(sel) {
    var o = '<option value="">Select a product…</option>';
    for (var i = 0; i < PRODUCTS.length; i++) { o += '<optgroup label="' + esc(PRODUCTS[i].fam) + '">';
      var it = PRODUCTS[i].items; for (var j = 0; j < it.length; j++) o += '<option value="' + esc(it[j].id) + '"' + (it[j].id === sel ? ' selected' : '') + '>' + esc(it[j].name) + '</option>';
      o += '</optgroup>'; }
    return o;
  }
  function optRoles(sel) { var o = '<option value="">Role…</option>';
    for (var i = 0; i < ROLES.length; i++) o += '<option value="' + esc(ROLES[i].id) + '"' + (ROLES[i].id === sel ? ' selected' : '') + '>' + esc(ROLES[i].label) + '</option>'; return o; }
  function optCands(sel) { return candidateOptions(sel, 'Format (optional)'); }
  function blendComplete() { var b = S.blend; return !!(b.product && b.ingredients.length >= BLEND_MIN && b.ingredients.every(function (i) { return i.role && (i.botanical || '').trim(); })); }
  // The blend control surface is a pure renderer (blendControlsHtml) so its numbering and row handling are
  // regression-tested directly; this binds it to the live state.
  function blendControls() { return blendControlsHtml(S.blend); }
  function controls() { return pillsControls(S); }
  // Single-mode Copy Details lives on the CALLER now (moved out of the card renderers so a blend card never
  // carries a per-card Copy Details — blend has ONE blend-level Copy Details). Shown for resolved/gated only.
  function singleCopy(resp) { return (resp && !resp.guidance_status && !resp.clarification_required) ? copyDetailsBtn() : ''; }
  function workHtml() {
    if (S.view === 'loading') return renderLoading('Generating specification…');
    if (S.view === 'loading2') return renderResponse(S.resp) + singleCopy(S.resp) + renderLoading('Checking sourcing options…');
    if (S.view === 'degraded') return renderDegraded(S.message);
    if (S.view === 'notavailable') return renderNotAvailable(S.message);
    if (S.view === 'ratelimited') return renderRateLimited(true); // single mode: enquiry link ON (G4 item 4)
    if (S.view === 'sourcing' && S.proc) return singleResultHtml(S, renderStage2Result(S.proc));
    if (S.view === 'result' && S.resp) return singleResultHtml(S);
    if (S.wiz.family) return '';   // the landing helper belongs to the landing only
    return '<p class="bb-empty">Pick a product, a role and a botanical, then get the recommended ingredient form — and check a format you already have in mind before you buy.</p>';
  }
  // Blend is entered by CONTINUING, never by a mode toggle: the completed first pass becomes row 1 and the
  // product becomes the row context. Every existing blend guarantee (row-context, stale-discard, Stage-2
  // suppression) is untouched because the blend renderer and runner are reused as-is.
  function wizAddAnother() {
    var b = S.blend;
    if (S.mode === 'build') {                       // already blending — open one more row
      if (b.ingredients.length >= BLEND_MAX) return;
      b.ingredients.push(blendNewRow()); draw(); return;
    }
    b.product = S.product;                          // the completed single pass becomes ingredient 1
    var first = blendNewRow();
    first.product = b.product; first.role = S.role; first.botanical = S.botanical; first.candidate = S.candidate;
    first.resp = S.resp; first.status = 'done'; first.wiz.cov = 'covered';
    var second = blendNewRow(); second.product = b.product;
    b.ingredients = [first, second];
    S.mode = 'build';
    try { sessionStorage.setItem('hb_mode', 'build'); } catch (e) {}
    draw();
  }
  // Leaving the blend returns to a clean single-ingredient journey.
  function blendRestart() {
    S.mode = 'quick'; try { sessionStorage.setItem('hb_mode', 'quick'); } catch (x) {}
    S.blend = { product: '', ingredients: [], nextId: 1, seq: 0, running: false };
    wizInvalidate(S, 'family'); S.wiz.family = ''; draw();
  }
  // Run ONE blend row through the existing runner, preserving its per-row stale token.
  function runRow(ing) {
    if (!(ing.role && (ing.botanical || '').trim())) return;
    blendInvalidateRow(ing); draw();
    runOne(ing).then(function () { draw(); });
  }
  function buildStub() { return '<div class="bb-card"><div class="bb-card-head">Full blend builder</div><p class="bb-card-msg">' + esc(BUILD_STUB) + '</p></div>'; }

  function draw() {
    // No mode toggle in the pills-first shell — blend is entered by "+ Add another ingredient" after a
    // result. The legacy shell buttons are hidden here rather than removed from the Liquid, so the shell
    // stays deployable unchanged and no governed behaviour moves.
    var mw = document.querySelector('.bb-modes'); if (mw) mw.style.display = 'none';
    var modes = document.querySelectorAll('.bb-mode');
    for (var i = 0; i < modes.length; i++) modes[i].classList.toggle('on', modes[i].getAttribute('data-mode') === S.mode);
    var C = document.getElementById('bb-controls'), W = document.getElementById('bb-work');
    if (S.mode === 'build') { C.innerHTML = blendControls(); W.innerHTML = renderBlend(S.blend); return; }
    C.innerHTML = controls();
    W.innerHTML = workHtml();
  }
  // Re-render ONLY the results area — preserves input focus while typing a botanical. And a targeted Run-button
  // enable/disable, so a keystroke doesn't rebuild the inputs.
  function drawBlendResults() { var W = document.getElementById('bb-work'); if (W) W.innerHTML = renderBlend(S.blend); }
  function updateRunBtn() { var btn = document.getElementById('bb-run-blend'); if (btn) btn.disabled = !(blendComplete() && !S.blend.running); }

  // CAPTURE-AT-OPEN (owner ruling). The modal is a detached singleton that outlives the row it was opened
  // from, so it captures its context once at open and never re-reads S.blend afterwards: it must keep
  // describing the action the buyer actually initiated, even if the blend is edited while it is open.
  // Both the visible body and [data-enq-copy] read this same captured value, so they can never disagree.
  var enquiryCtx = null;
  function enquiryText() { return enquiryLines(enquiryCtx || singleContext(S)); }
  // BUG 2: the enquiry route must NEVER be a dead control. A bare `mailto:` silently no-ops when the browser
  // has no mail handler (exactly the live symptom). The panel therefore does something visible without any
  // mail client: it shows the prefilled details and offers "Copy details".
  // G3 (owner ruling): the "Open in email" anchor is REMOVED rather than repaired — a mailto depends on an
  // OS mail handler the buyer may not have, so it could not be made reliable. "Copy details" is unchanged
  // and remains the enquiry route. Nothing shared was removed: enquiryText()/enquiryLines() still build the
  // context block below and the clipboard text.
  function closeEnquiry() { var e = document.getElementById('bb-enq'); if (e) e.remove(); enquiryCtx = null; }
  function openEnquiry(ctx) {
    closeEnquiry();            // clears any previous capture
    enquiryCtx = ctx || singleContext(S);
    var m = document.createElement('div');
    m.className = 'bb-modal'; m.id = 'bb-enq'; m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true');
    m.innerHTML = '<div class="bb-mb"><div class="bb-mt"><h3>Ask Herbuno to source this</h3>' +
      '<button class="bb-x" data-enq-close="1" aria-label="Close">×</button></div>' +
      '<div class="bb-mbd"><div class="bb-ctx">' + esc(enquiryText()).replace(/\n/g, '<br>') + '</div>' +
      '<p class="bb-priv">Send these details to our sourcing team and we’ll get back to you.</p>' +
      '<div class="bb-actions">' +
      '<button class="bb-btn o" data-enq-copy="1">Copy details</button></div></div></div>';
    root.appendChild(m);
  }
  function postJson(url, body) {
    return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.status === 429 ? { rate: true } : r.json().then(function (j) { return { status: r.status, body: j }; }); });
  }
  // Q2 -> Worker coverage check. Goes through the ordinary SPEC route and therefore the ordinary G4
  // limiter. The generation guard is the same one submitSpec uses: if the buyer changed product or role
  // while this was in flight, the reply is discarded and can neither advance nor terminate the new journey.
  // st is S in single mode and the ACTIVE BLEND ROW in build mode. A row carries the same
  // { product, role, wiz, gen } fields, so the coverage verdict, the generation guard and the governed
  // uncovered outcome behave identically per ingredient.
  function submitPreflight(st) {
    st = st || S;
    if (!(st.product && st.role)) return;
    st.wiz.cov = 'pending'; st.resp = null; st.message = '';
    if (st === S) { S.proc = null; S.view = 'input'; } else { st.status = 'idle'; }
    draw();
    var gen = st.gen;
    var stale = function () { return gen !== st.gen || (st !== S && S.blend.ingredients.indexOf(st) === -1); };
    postJson(SPEC_ENDPOINT, buildPreflightBody(st)).then(function (r) {
      if (stale()) return;                             // superseded question or removed row — drop it
      if (r.rate) { st.wiz.cov = ''; if (st === S) S.view = 'ratelimited'; else st.status = 'ratelimited'; return draw(); }
      // A 400 is NEVER read as a positive capability signal. The Worker is deployed before this asset
      // (see README "Deploy order"), so a preflight rejection means something is genuinely wrong and the
      // journey must not advance to Q3 on the strength of an error.
      if (r.status !== 200 || !apiCompatible(r.body)) { st.wiz.cov = '';
        if (st === S) { S.view = 'degraded'; S.message = (r.body && r.body.message) || DEGRADED_FALLBACK; }
        else { st.status = 'degraded'; st.message = (r.body && r.body.message) || DEGRADED_FALLBACK; }
        return draw(); }
      // UNCOVERED: the governed not_available_for_product outcome, unchanged, per ingredient.
      // Neither Q3 nor Q4 is produced for that row.
      if (r.body.guidance_status) { st.wiz.cov = 'uncovered'; st.resp = r.body;
        if (st === S) S.view = 'result'; else st.status = 'done';
        return draw(); }
      st.wiz.cov = 'covered'; if (st === S) S.view = 'input'; draw();
    }).catch(function () { if (stale()) return; st.wiz.cov = '';
      if (st === S) { S.view = 'degraded'; S.message = DEGRADED_FALLBACK; }
      else { st.status = 'degraded'; st.message = DEGRADED_FALLBACK; }
      draw(); });
  }
  function submitSpec() {
    if (!(S.product && S.role && (S.botanical || '').trim())) return;
    S.view = 'loading'; S.resp = null; S.proc = null; draw();
    // Generation guard for single mode (blend rows already have their own per-row token). Any answer change
    // bumps S.gen via wizInvalidate(), so a reply that arrives for a superseded question is dropped.
    var gen = S.gen;
    postJson(SPEC_ENDPOINT, buildSpecBody(S)).then(function (r) {
      if (gen !== S.gen) return;
      if (r.rate) { S.view = 'ratelimited'; return draw(); }
      // 400 = unmapped product×role / rejected input — a guidance case, NOT degraded (degraded is 5xx only)
      if (r.status === 400) { S.view = 'notavailable'; S.message = (r.body && r.body.detail) || ''; return draw(); }
      if (r.status !== 200 || !apiCompatible(r.body)) { S.view = 'degraded'; S.message = (r.body && r.body.message) || DEGRADED_FALLBACK; return draw(); }
      S.resp = r.body; S.view = 'result'; draw();
    }).catch(function () { if (gen !== S.gen) return; S.view = 'degraded'; S.message = DEGRADED_FALLBACK; draw(); });
  }
  function submitSourcing() {
    if (!S.resp || S.resp.identity_status !== 'resolved' || !S.resp.specification_token) return;
    S.view = 'loading2'; draw();
    postJson(PROC_ENDPOINT, { specification_token: S.resp.specification_token }).then(function (r) {
      if (r.rate) { S.view = 'ratelimited'; return draw(); }
      if (r.status !== 200 || !apiCompatible(r.body)) { S.view = 'degraded'; S.message = (r.body && r.body.message) || DEGRADED_FALLBACK; return draw(); }
      S.proc = r.body; S.view = 'sourcing'; draw();
    }).catch(function () { S.view = 'degraded'; S.message = DEGRADED_FALLBACK; draw(); });
  }

  function blendFindRow(id) { for (var i = 0; i < S.blend.ingredients.length; i++) if (S.blend.ingredients[i].id === id) return S.blend.ingredients[i]; return null; }
  // RESULT INVALIDATION: bump the token (drops any in-flight response for this row) and clear its result.
  function blendInvalidateRow(ing) { ing.token = ++S.blend.seq; ing.status = 'idle'; ing.resp = null; ing.message = ''; }
  // One ingredient's Stage-1 call. STALE-RESULT PROTECTION: a response is applied ONLY if the row still exists
  // and its token is unchanged (not edited / removed / re-run since this call started).
  function runOne(ing) {
    var myToken = ++S.blend.seq; ing.token = myToken; ing.status = 'loading';
    return postJson(SPEC_ENDPOINT, buildSpecBody({ product: S.blend.product, role: ing.role, botanical: (ing.botanical || '').trim(), candidate: ing.candidate }))
      .then(function (r) {
        if (ing.token !== myToken || S.blend.ingredients.indexOf(ing) === -1) return; // stale — drop
        if (r.rate) ing.status = 'ratelimited';
        else if (r.status === 400) { ing.status = 'notavailable'; ing.message = (r.body && r.body.detail) || ''; }
        else if (r.status !== 200 || !apiCompatible(r.body)) { ing.status = 'degraded'; ing.message = (r.body && r.body.message) || DEGRADED_FALLBACK; }
        else { ing.resp = r.body; ing.status = 'done'; }
        drawBlendResults();
      })
      .catch(function () { if (ing.token !== myToken || S.blend.ingredients.indexOf(ing) === -1) return; ing.status = 'degraded'; ing.message = DEGRADED_FALLBACK; drawBlendResults(); });
  }
  function runBlend() {
    var b = S.blend;
    if (b.running || !blendComplete()) return;
    b.running = true;
    b.ingredients.forEach(function (ing) { ing.status = 'pending'; ing.resp = null; ing.message = ''; });
    draw(); // disables all inputs, shows per-row loading
    runPool(b.ingredients.slice(), BLEND_CAP, runOne).then(function () { b.running = false; draw(); });
  }

  root.addEventListener('click', function (e) {
    if (e.target.id === 'bb-enq') { closeEnquiry(); return; } // click the modal backdrop to dismiss
    var bsum = e.target.closest ? e.target.closest('summary.bb-blend-h') : null;
    if (bsum) { var bdet = bsum.parentElement, bing = bdet && blendFindRow(bdet.getAttribute('data-blend-row'));
      if (bing) bing.uiOpen = !bdet.open;   // the click is about to toggle it
      return; }
    var md = e.target.closest ? e.target.closest('[data-mode]') : null;
    if (md) { S.mode = md.getAttribute('data-mode'); try { sessionStorage.setItem('hb_mode', S.mode); } catch (x) {} draw(); return; }
    // ---- pills-first wizard. Every branch invalidates dependent state before redrawing, so a changed
    // answer can never leave a stale downstream selection or a stale result on screen.
    var wz = e.target.closest ? e.target.closest('[data-wiz-family],[data-wiz-product],[data-wiz-role],[data-wiz-change],[data-wiz-form],[data-wiz-openform],[data-wiz-skipform],[data-wiz-addmore],[data-wiz-restart],[data-ing-change]') : null;
    if (wz) {
      // W is S in single mode and the ACTIVE BLEND ROW in build mode, so one set of handlers drives
      // both. Product remains single-mode-only: a blend never re-asks it.
      var g, W = wizTarget();
      if ((g = wz.getAttribute('data-wiz-family')) != null) { wizInvalidate(S, 'family'); S.wiz.family = g; S.wiz.step = 'product'; draw(); return; }
      if ((g = wz.getAttribute('data-wiz-product')) != null) { wizInvalidate(S, 'product'); S.wiz.family = wizFamilyOf(g); S.product = g; S.wiz.step = 'role'; draw(); return; }
      if ((g = wz.getAttribute('data-wiz-role')) != null) { wizInvalidate(W, 'role'); W.role = g; W.wiz.step = 'botanical'; submitPreflight(W); return; }
      if ((g = wz.getAttribute('data-wiz-change')) != null) {
        if (W !== S && g === 'family') { blendRestart(); return; }   // "Change" the product = start over
        wizInvalidate(W, g);
        if (g === 'form') { W.wiz.formOpen = true; if (W === S) S.view = 'input'; }
        if (W !== S) W.status = 'idle';
        draw(); return; }
      if (wz.getAttribute('data-wiz-openform') != null) { W.wiz.formOpen = true; draw(); return; }
      if (wz.getAttribute('data-wiz-skipform') != null) { W.wiz.formOpen = false; draw(); return; }
      if ((g = wz.getAttribute('data-wiz-form')) != null) { W.candidate = g; W.wiz.formOpen = false;
        if (W === S) submitSpec(); else runRow(W); return; }
      if ((g = wz.getAttribute('data-ing-change')) != null) {
        var CR = blendFindRow(g);
        if (CR) { wizInvalidate(CR, 'role'); blendInvalidateRow(CR); CR.wiz.step = 'role'; draw(); }
        return; }
      if (wz.getAttribute('data-wiz-addmore') != null) { wizAddAnother(); return; }
      if (wz.getAttribute('data-wiz-restart') != null) { blendRestart(); return; }
    }
    var t = e.target.closest ? e.target.closest('[data-sourcing],[data-backspec],[data-retry],[data-enquiry],[data-enq-close],[data-enq-copy],[data-copysel],[data-copydetails],[data-copyblend],[data-select-candidate],[data-ing-remove],#bb-go,#bb-add-ing,#bb-run-blend') : null;
    if (!t) return;
    // G2: resolve the CLICKED blend row ONCE, here. Downstream actions previously took no arguments and
    // closed over single-mode S, so a blend-row click acted on whatever the buyer had last done in single
    // mode. Resolving per-action is how data-select-candidate ended up the only correct branch; this is
    // the single resolution point. rowIng is null in single mode, which selects the single-mode context.
    var rowEl = t.closest ? t.closest('[data-blend-row]') : null;
    var rowIng = rowEl ? blendFindRow(rowEl.getAttribute('data-blend-row')) : null;
    var actionCtx = rowIng ? blendRowContext(S.blend, rowIng) : singleContext(S);
    if (t.id === 'bb-go') { var GT = wizTarget(); if (GT === S) submitSpec(); else runRow(GT); }
    else if (t.id === 'bb-run-blend') runBlend();
    else if (t.id === 'bb-add-ing') { if (S.blend.ingredients.length < BLEND_MAX && !S.blend.running) { S.blend.ingredients.push(blendNewRow()); draw(); } }
    else if (t.getAttribute('data-ing-remove') != null) { if (S.blend.ingredients.length > BLEND_MIN && !S.blend.running) { var rid = t.getAttribute('data-ing-remove'); S.blend.ingredients = S.blend.ingredients.filter(function (x) { return x.id !== rid; }); draw(); } }
    else if (t.getAttribute('data-copyblend') != null) { try { navigator.clipboard.writeText(copyBlendText(S.blend, productName(S.blend.product))); t.textContent = 'Copied'; } catch (x) {} }
    else if (t.getAttribute('data-select-candidate') != null) {
      // Candidate-selection gate. In BLEND mode (inside a [data-blend-row]) invalidate + re-run ONLY that
      // ingredient; in single mode re-submit the one query. Latin binomials are never safeguard-blocked.
      var pick = t.getAttribute('data-select-candidate');
      var brow = t.closest ? t.closest('[data-blend-row]') : null;
      if (brow) { var bing = blendFindRow(brow.getAttribute('data-blend-row')); if (bing && !S.blend.running) { bing.botanical = pick; runOne(bing); drawBlendResults(); } }
      else { S.botanical = pick; submitSpec(); }
    }
    else if (t.getAttribute('data-sourcing') != null) submitSourcing();
    else if (t.getAttribute('data-backspec') != null) { S.view = 'result'; S.proc = null; draw(); }
    // A degraded BLEND row retries only itself, through runOne(ing) — the existing row-scoped path that
    // already carries identity/generation protection. Blend retry must NOT go through submitSpec(), which
    // re-runs the single-mode query and writes state that build mode never renders.
    else if (t.getAttribute('data-retry') != null) { if (rowIng) { if (!S.blend.running) { runOne(rowIng); drawBlendResults(); } } else submitSpec(); }
    else if (t.getAttribute('data-enquiry') != null) { openEnquiry(actionCtx); }
    else if (t.getAttribute('data-enq-close') != null) { closeEnquiry(); }
    else if (t.getAttribute('data-enq-copy') != null) { try { navigator.clipboard.writeText(enquiryText()); t.textContent = 'Copied'; } catch (x) {} }
    else if (t.getAttribute('data-copydetails') != null) {
      try { navigator.clipboard.writeText(copyText(S.resp, { product: S.product, role: S.role, botanical: S.botanical })); t.textContent = 'Copied'; } catch (x) {}
    }
    else if (t.getAttribute('data-copysel') != null) {
      try { navigator.clipboard.writeText(copySelectionText(actionCtx)); t.textContent = 'Copied'; } catch (x) {}
    }
  });
  root.addEventListener('input', function (e) {
    if (e.target.id === 'bb-bot') { var BT = wizTarget(); BT.botanical = e.target.value;
      var go = document.getElementById('bb-go'); if (go) go.disabled = !(BT.product && BT.role && (BT.botanical || '').trim()); return; }
    if (e.target.id === 'bb-bot-en') { return; }
    if (e.target.getAttribute && e.target.getAttribute('data-f') === 'botanical' && !S.blend.running) {
      var brow = e.target.closest('[data-ing-row]'); if (!brow) return;
      var ing = blendFindRow(brow.getAttribute('data-ing-row'));
      if (ing) { ing.botanical = e.target.value; blendInvalidateRow(ing); drawBlendResults(); updateRunBtn(); } // clears THIS row's result; keeps input focus
    }
  });
  root.addEventListener('change', function (e) {
    var t = e.target;
    // G4 item 5 — the format-in-mind is a property of the CELL being asked about, so changing product or
    // role clears it. Previously S.candidate persisted silently: a buyer who moved to a new product+role
    // kept sending a format chosen minutes earlier, with no indication why the candidate strip reappeared.
    // It is deliberately KEPT across mode switches (owner ruling) and across a botanical change, since
    // neither changes the cell. Blend rows are unaffected: each carries its own ing.candidate.
    if (t.id === 'bb-prod') { S.product = t.value; S.candidate = ''; S.view = 'input'; S.resp = null; S.proc = null; draw(); }
    else if (t.id === 'bb-role') { S.role = t.value; S.candidate = ''; S.view = 'input'; S.resp = null; S.proc = null; draw(); }
    else if (t.id === 'bb-cand') { S.candidate = t.value; }
    else if (t.id === 'bb-blend-prod') { // changing the shared product clears ALL ingredient results
      if (S.blend.running) return;
      S.blend.product = t.value; S.blend.ingredients.forEach(function (ing) { blendInvalidateRow(ing); }); draw();
    }
    else if (t.getAttribute && (t.getAttribute('data-f') === 'role' || t.getAttribute('data-f') === 'candidate')) { // per-row change clears THAT row
      if (S.blend.running) return;
      var brow2 = t.closest('[data-ing-row]'); if (!brow2) return;
      var ing2 = blendFindRow(brow2.getAttribute('data-ing-row'));
      if (ing2) { ing2[t.getAttribute('data-f')] = t.value; blendInvalidateRow(ing2); draw(); }
    }
  });

  draw();
})();
